import { useState, useMemo, useCallback, useEffect } from "react";
import { cn } from "~/widgets/lib/utils";
import type {
  TimetableCourse,
  TimetableLocation,
  TimetableTutor,
} from "~/widgets/lib/api";
import { MobileFilterOverlay, MobileDateRangeOverlay } from "./MobileFilterOverlay";

// Keep this in sync with LONDON_LADY_INITIAL_SESSION_LIMIT in Timetable.tsx.
const LONDON_LADY_INITIAL_SESSION_LIMIT = 30;

type AvailabilityFilter = "all" | "sold-out" | "available" | "few-spots";

type DateRange = {
  from: Date;
  to: Date;
};

type TimetableFiltersProps = {
  courses: TimetableCourse[];
  locations: TimetableLocation[];
  tutors: TimetableTutor[];
  selectedCourseIds: string[];
  selectedLocationIds: string[];
  selectedTutorIds: string[];
  selectedAvailability: AvailabilityFilter;
  dateRange: DateRange;
  onCourseChange: (ids: string[]) => void;
  onLocationChange: (ids: string[]) => void;
  onTutorChange: (ids: string[]) => void;
  onAvailabilityChange: (value: AvailabilityFilter) => void;
  onDateRangeChange: (range: DateRange) => void;
  onReset: () => void;
  /** Primary color for buttons and accents */
  primaryColor?: string;
  /** Business slug for loading theme overrides */
  businessSlug?: string;
  /** API URL for loading theme overrides */
  apiUrl?: string;
  /** Show week-based navigation instead of date range picker */
  showWeekFilter?: boolean;
  /** Whether the week filter is currently constraining results (London Lady mode) */
  weekFilterActive?: boolean;
  /** Toggle the week filter active state (London Lady mode) */
  onWeekFilterActiveChange?: (active: boolean) => void;
};

const AVAILABILITY_OPTIONS: { value: AvailabilityFilter; label: string }[] = [
  { value: "all", label: "All availability" },
  { value: "available", label: "Available" },
  { value: "few-spots", label: "Few spots left" },
  { value: "sold-out", label: "Sold out" },
];

// Date helper functions
function addWeeks(date: Date, weeks: number): Date {
  const result = new Date(date);
  result.setDate(result.getDate() + weeks * 7);
  return result;
}

function addMonths(date: Date, months: number): Date {
  const result = new Date(date);
  result.setMonth(result.getMonth() + months);
  return result;
}

function isSameDay(date1: Date, date2: Date): boolean {
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  );
}

function startOfWeek(date: Date): Date {
  const result = new Date(date);
  result.setDate(result.getDate() - result.getDay());
  result.setHours(0, 0, 0, 0);
  return result;
}

function endOfWeek(date: Date): Date {
  const result = new Date(date);
  result.setDate(result.getDate() + (6 - result.getDay()));
  result.setHours(23, 59, 59, 999);
  return result;
}

function isSameWeek(date1: Date, date2: Date): boolean {
  return startOfWeek(date1).getTime() === startOfWeek(date2).getTime();
}

function formatDate(date: Date): string {
  return date.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
  });
}

function formatDateRange(from: Date, to: Date): string {
  const fromStr = formatDate(from);
  const toYear = to.getFullYear();
  const toStr = to.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
  return `${fromStr} - ${toStr}`;
}

// Icons as inline SVG to avoid external dependencies
function FilterIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
    </svg>
  );
}

function ListIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <line x1="8" x2="21" y1="6" y2="6" />
      <line x1="8" x2="21" y1="12" y2="12" />
      <line x1="8" x2="21" y1="18" y2="18" />
      <line x1="3" x2="3.01" y1="6" y2="6" />
      <line x1="3" x2="3.01" y1="12" y2="12" />
      <line x1="3" x2="3.01" y1="18" y2="18" />
    </svg>
  );
}

function TicketIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2Z" />
      <path d="M13 5v2" />
      <path d="M13 17v2" />
      <path d="M13 11v2" />
    </svg>
  );
}

function MapIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" />
      <circle cx="12" cy="10" r="3" />
    </svg>
  );
}

function UserIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  );
}

function CalendarIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect width="18" height="18" x="3" y="4" rx="2" ry="2" />
      <line x1="16" x2="16" y1="2" y2="6" />
      <line x1="8" x2="8" y1="2" y2="6" />
      <line x1="3" x2="21" y1="10" y2="10" />
    </svg>
  );
}

function CalendarRangeIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect width="18" height="18" x="3" y="4" rx="2" ry="2" />
      <line x1="16" x2="16" y1="2" y2="6" />
      <line x1="8" x2="8" y1="2" y2="6" />
      <line x1="3" x2="21" y1="10" y2="10" />
      <path d="M17 14h-6" />
      <path d="M13 18H7" />
      <path d="M7 14h.01" />
      <path d="M17 18h.01" />
    </svg>
  );
}

function ChevronDownIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}

function ChevronLeftIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="m15 18-6-6 6-6" />
    </svg>
  );
}

function ChevronRightIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="m9 18 6-6-6-6" />
    </svg>
  );
}

function XIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M18 6 6 18" />
      <path d="m6 6 12 12" />
    </svg>
  );
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      width="12"
      height="12"
      viewBox="0 0 12 12"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
    >
      <path d="M2 6l3 3 5-5" />
    </svg>
  );
}

// Simple dropdown component for desktop
function Dropdown({
  label,
  options,
  selectedValues,
  onChange,
  placeholder,
  multiple = false,
}: {
  label: string;
  options: { value: string; label: string }[];
  selectedValues: string[];
  onChange: (values: string[]) => void;
  placeholder: string;
  multiple?: boolean;
}) {
  const [isOpen, setIsOpen] = useState(false);

  const displayText = useMemo(() => {
    if (selectedValues.length === 0) return placeholder;
    if (selectedValues.length === 1) {
      const option = options.find((o) => o.value === selectedValues[0]);
      return option?.label || placeholder;
    }
    return `${selectedValues.length} selected`;
  }, [selectedValues, options, placeholder]);

  const handleSelect = (value: string) => {
    if (multiple) {
      if (selectedValues.includes(value)) {
        onChange(selectedValues.filter((v) => v !== value));
      } else {
        onChange([...selectedValues, value]);
      }
    } else {
      onChange(value ? [value] : []);
      setIsOpen(false);
    }
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          "flex items-center gap-2 px-3 py-2 text-sm bg-white border rounded-full",
          "hover:bg-gray-50 transition-colors min-w-[120px] max-w-[200px]",
          selectedValues.length > 0
            ? "border-gray-400 text-gray-900"
            : "border-gray-200 text-gray-600",
        )}
      >
        <span className="truncate flex-1 text-left">{displayText}</span>
        <ChevronDownIcon
          className={cn(
            "shrink-0 transition-transform",
            isOpen && "rotate-180",
          )}
        />
      </button>

      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
          />

          {/* Dropdown menu */}
          <div className="absolute top-full left-0 mt-1 z-50 min-w-[200px] max-h-[300px] overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg">
            <div className="p-1">
              {/* Clear option for single select */}
              {!multiple && selectedValues.length > 0 && (
                <button
                  type="button"
                  onClick={() => handleSelect("")}
                  className="w-full px-3 py-2 text-left text-sm text-gray-500 hover:bg-gray-100 rounded"
                >
                  {placeholder}
                </button>
              )}

              {options.map((option) => {
                const isSelected = selectedValues.includes(option.value);
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => handleSelect(option.value)}
                    className={cn(
                      "w-full px-3 py-2 text-left text-sm rounded flex items-center gap-2",
                      isSelected
                        ? "bg-gray-100 text-gray-900 font-medium"
                        : "text-gray-700 hover:bg-gray-50",
                    )}
                  >
                    {multiple && (
                      <span
                        className={cn(
                          "w-4 h-4 border rounded flex items-center justify-center shrink-0",
                          isSelected
                            ? "bg-gray-900 border-gray-900 text-white"
                            : "border-gray-300",
                        )}
                      >
                        {isSelected && <CheckIcon />}
                      </span>
                    )}
                    <span className="truncate">{option.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// Filter button component for mobile menu
function FilterMenuButton({
  icon: Icon,
  label,
  value,
  onClick,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full flex items-center justify-between px-4 py-3 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
    >
      <div className="flex items-center gap-3">
        <Icon className="h-4 w-4 text-gray-500" />
        <div className="flex flex-col items-start">
          <span className="text-xs text-gray-500">{label}</span>
          <span className="text-sm font-medium text-gray-900">{value}</span>
        </div>
      </div>
      <ChevronDownIcon className="h-4 w-4 text-gray-400" />
    </button>
  );
}

// Search icon for filter sheets
function SearchIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="11" cy="11" r="8" />
      <path d="m21 21-4.3-4.3" />
    </svg>
  );
}

// Selected chip component
function SelectedChip({
  label,
  onRemove,
}: {
  label: string;
  onRemove: () => void;
}) {
  return (
    <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-300 rounded-full text-sm">
      <span className="truncate max-w-[200px]">{label}</span>
      <button
        type="button"
        onClick={onRemove}
        className="p-0.5 hover:bg-gray-100 rounded-full"
      >
        <XIcon className="h-3 w-3" />
      </button>
    </div>
  );
}

// Nested filter sheet component with enhanced UX
// Note: This is rendered inside a FilterOverlayPortal so no fixed positioning needed
function NestedFilterSheet({
  isOpen,
  onClose,
  title,
  children,
  showSearch = false,
  searchPlaceholder = "Search...",
  searchValue = "",
  onSearchChange,
  selectedItems = [],
  onRemoveItem,
  onClearAll,
  onDone,
  showFooter = false,
  primaryColor,
}: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  showSearch?: boolean;
  searchPlaceholder?: string;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  selectedItems?: { value: string; label: string }[];
  onRemoveItem?: (value: string) => void;
  onClearAll?: () => void;
  onDone?: () => void;
  showFooter?: boolean;
  primaryColor?: string;
}) {
  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop - clickable area to close */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      {/* Sheet - positioned at top */}
      <div className="absolute inset-x-0 top-0 bg-white rounded-b-2xl shadow-xl max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center gap-3 p-4 border-b">
          <button
            type="button"
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full -ml-2"
          >
            <ChevronLeftIcon className="h-5 w-5" />
          </button>
          <h2 className="text-lg font-semibold">{title}</h2>
        </div>

        {/* Search Input */}
        {showSearch && onSearchChange && (
          <div className="px-4 pt-4">
            <div className="relative">
              <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={searchValue}
                onChange={(e) => onSearchChange(e.target.value)}
                placeholder={searchPlaceholder}
                className="w-full pl-9 pr-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
              />
            </div>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">{children}</div>

        {/* Selected Items & Footer */}
        {showFooter && (
          <div className="border-t">
            {/* Selected Items Chips */}
            {selectedItems.length > 0 && (
              <div className="px-4 pt-4 pb-2">
                <div className="flex flex-wrap gap-2">
                  {selectedItems.map((item) => (
                    <SelectedChip
                      key={item.value}
                      label={item.label}
                      onRemove={() => onRemoveItem?.(item.value)}
                    />
                  ))}
                </div>
                {/* Clear all link */}
                {onClearAll && selectedItems.length > 0 && (
                  <button
                    type="button"
                    onClick={onClearAll}
                    className="mt-3 text-sm text-gray-500 hover:text-gray-700"
                  >
                    Clear all
                  </button>
                )}
              </div>
            )}

            {/* Done Button */}
            {onDone && (
              <div className="p-4">
                <button
                  type="button"
                  onClick={onDone}
                  className="w-full px-4 py-3 text-sm font-medium text-white rounded-lg transition-colors"
                  style={{ backgroundColor: primaryColor || "#03c2b9" }}
                >
                  Done
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
}

// Simple calendar component
function SimpleCalendar({
  selectedRange,
  onRangeChange,
  primaryColor,
  tempStart: tempStartProp,
  onTempStartChange,
}: {
  selectedRange: DateRange;
  onRangeChange: (range: DateRange) => void;
  primaryColor?: string;
  /** Optional controlled tempStart so the parent can commit it on Apply */
  tempStart?: Date | null;
  onTempStartChange?: (date: Date | null) => void;
}) {
  const [currentMonth, setCurrentMonth] = useState(
    new Date(selectedRange.from.getFullYear(), selectedRange.from.getMonth(), 1),
  );
  const [selectingStart, setSelectingStart] = useState(true);
  const [internalTempStart, setInternalTempStart] = useState<Date | null>(null);
  const tempStart = tempStartProp !== undefined ? tempStartProp : internalTempStart;
  const setTempStart = (date: Date | null) => {
    setInternalTempStart(date);
    onTempStartChange?.(date);
  };

  // Sync calendar month when selected range changes (e.g., from Quick Select)
  useEffect(() => {
    const rangeMonth = new Date(selectedRange.from.getFullYear(), selectedRange.from.getMonth(), 1);
    if (rangeMonth.getTime() !== currentMonth.getTime()) {
      setCurrentMonth(rangeMonth);
    }
    // Reset selection state when range changes externally
    setTempStart(null);
    setSelectingStart(true);
  }, [selectedRange.from.getTime()]);

  const daysInMonth = new Date(
    currentMonth.getFullYear(),
    currentMonth.getMonth() + 1,
    0,
  ).getDate();

  const firstDayOfMonth = new Date(
    currentMonth.getFullYear(),
    currentMonth.getMonth(),
    1,
  ).getDay();

  const monthName = currentMonth.toLocaleDateString("en-GB", {
    month: "long",
    year: "numeric",
  });

  const days = [];
  for (let i = 0; i < firstDayOfMonth; i++) {
    days.push(null);
  }
  for (let i = 1; i <= daysInMonth; i++) {
    days.push(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth(), i),
    );
  }

  const isInRange = (date: Date) => {
    if (!date) return false;
    const start = tempStart || selectedRange.from;
    const end = selectedRange.to;
    return date >= start && date <= end;
  };

  const isStart = (date: Date) => {
    if (!date) return false;
    const start = tempStart || selectedRange.from;
    return isSameDay(date, start);
  };

  const isEnd = (date: Date) => {
    if (!date) return false;
    return isSameDay(date, selectedRange.to);
  };

  const isPast = (date: Date) => {
    if (!date) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };

  const handleDayClick = (date: Date) => {
    if (!date || isPast(date)) return;

    if (selectingStart) {
      setTempStart(date);
      setSelectingStart(false);
    } else {
      const start = tempStart || selectedRange.from;
      if (date < start) {
        onRangeChange({ from: date, to: start });
      } else {
        onRangeChange({ from: start, to: date });
      }
      setTempStart(null);
      setSelectingStart(true);
    }
  };

  const prevMonth = () => {
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1),
    );
  };

  const nextMonth = () => {
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1),
    );
  };

  return (
    <div className="p-2">
      {/* Month navigation */}
      <div className="flex items-center justify-between mb-4">
        <button
          type="button"
          onClick={prevMonth}
          className="p-2 hover:bg-gray-100 rounded-full"
        >
          <ChevronLeftIcon />
        </button>
        <span className="font-medium">{monthName}</span>
        <button
          type="button"
          onClick={nextMonth}
          className="p-2 hover:bg-gray-100 rounded-full"
        >
          <ChevronRightIcon />
        </button>
      </div>

      {/* Weekday headers */}
      <div className="grid grid-cols-7 gap-1 mb-2">
        {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((day) => (
          <div
            key={day}
            className="text-center text-xs font-medium text-gray-500 py-1"
          >
            {day}
          </div>
        ))}
      </div>

      {/* Days grid */}
      <div className="grid grid-cols-7 gap-1">
        {days.map((date, index) => {
          if (!date) {
            return <div key={`empty-${index}`} className="h-9" />;
          }

          const isDisabled = isPast(date);
          const inRange = isInRange(date);
          const isRangeStart = isStart(date);
          const isRangeEnd = isEnd(date);

          return (
            <button
              key={date.toISOString()}
              type="button"
              disabled={isDisabled}
              onClick={() => handleDayClick(date)}
              className={cn(
                "h-9 w-full text-sm rounded-md transition-colors",
                isDisabled && "text-gray-300 cursor-not-allowed",
                !isDisabled && !inRange && "hover:bg-gray-100 text-gray-900",
                inRange && !isRangeStart && !isRangeEnd && "bg-gray-100 text-gray-900",
                (isRangeStart || isRangeEnd) && "text-white",
              )}
              style={(isRangeStart || isRangeEnd) ? { backgroundColor: primaryColor || "#03c2b9" } : undefined}
            >
              {date.getDate()}
            </button>
          );
        })}
      </div>

      {/* Selection hint */}
      <p className="text-xs text-gray-500 mt-3 text-center">
        {selectingStart
          ? "Select start date"
          : "Select end date"}
      </p>
    </div>
  );
}

// Date Range Sheet component
function DateRangeSheet({
  isOpen,
  onClose,
  dateRange,
  onDateRangeChange,
  side = "top",
  primaryColor,
}: {
  isOpen: boolean;
  onClose: () => void;
  dateRange: DateRange;
  onDateRangeChange: (range: DateRange) => void;
  side?: "top" | "right";
  primaryColor?: string;
}) {
  const [localRange, setLocalRange] = useState(dateRange);
  const [tempStart, setTempStart] = useState<Date | null>(null);

  // Sync local state when sheet opens
  useEffect(() => {
    if (isOpen) {
      setLocalRange(dateRange);
      setTempStart(null);
    }
  }, [isOpen, dateRange]);

  if (!isOpen) return null;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const isToday = isSameDay(localRange.from, today) && isSameDay(localRange.to, today);
  const isTwoWeeks =
    isSameDay(localRange.from, today) && isSameDay(localRange.to, addWeeks(today, 2));
  const isThreeMonths =
    isSameDay(localRange.from, today) && isSameDay(localRange.to, addMonths(today, 3));
  const isSixMonths =
    isSameDay(localRange.from, today) && isSameDay(localRange.to, addMonths(today, 6));

  const setQuickRange = (weeks?: number, months?: number, isToday?: boolean) => {
    const from = new Date();
    from.setHours(0, 0, 0, 0);
    let to: Date;

    if (isToday) {
      to = new Date(from);
    } else if (weeks) {
      to = addWeeks(from, weeks);
    } else if (months) {
      to = addMonths(from, months);
    } else {
      to = new Date(from);
    }

    setLocalRange({ from, to });
  };

  const handleApply = () => {
    // If the user only clicked a single date (tempStart set, no second click yet),
    // treat that single click as the new start, extending end if needed.
    const rangeToApply = tempStart
      ? { from: tempStart, to: tempStart > localRange.to ? tempStart : localRange.to }
      : localRange;
    onDateRangeChange(rangeToApply);
    onClose();
  };

  const handleReset = () => {
    const from = new Date();
    from.setHours(0, 0, 0, 0);
    const to = addMonths(from, 12);
    setLocalRange({ from, to });
  };

  const QuickSelectButton = ({
    label,
    isActive,
    onClick,
    icon: Icon,
    primaryColor: btnPrimaryColor,
  }: {
    label: string;
    isActive: boolean;
    onClick: () => void;
    icon: React.ComponentType<{ className?: string }>;
    primaryColor?: string;
  }) => (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex items-center gap-2 px-3 py-2 text-sm rounded-lg border transition-colors",
        isActive
          ? "text-white border-transparent"
          : "border-gray-200 hover:bg-gray-50 text-gray-900 bg-white",
      )}
      style={isActive ? { backgroundColor: btnPrimaryColor || "#03c2b9" } : undefined}
    >
      <Icon className="h-4 w-4" />
      {label}
    </button>
  );

  // Right side sheet for desktop
  if (side === "right") {
    return (
      <>
        <div className="fixed inset-0 bg-black/50 z-50" onClick={onClose} />
        <div className="fixed inset-y-0 right-0 z-50 w-[340px] bg-white shadow-xl flex flex-col animate-in slide-in-from-right duration-200">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b">
            <h2 className="text-lg font-semibold">Select Date Range</h2>
            <button
              type="button"
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full"
            >
              <XIcon className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {/* Quick Select */}
            <div className="space-y-2">
              <h4 className="font-medium text-sm">Quick Select</h4>
              <div className="grid grid-cols-2 gap-2">
                <QuickSelectButton
                  label="Today"
                  isActive={isToday}
                  onClick={() => setQuickRange(undefined, undefined, true)}
                  icon={CalendarIcon}
                />
                <QuickSelectButton
                  label="Next 2 Weeks"
                  isActive={isTwoWeeks}
                  onClick={() => setQuickRange(2)}
                  icon={CalendarRangeIcon}
                />
                <QuickSelectButton
                  label="Next 3 Months"
                  isActive={isThreeMonths}
                  onClick={() => setQuickRange(undefined, 3)}
                  icon={CalendarRangeIcon}
                />
                <QuickSelectButton
                  label="Next 6 Months"
                  isActive={isSixMonths}
                  onClick={() => setQuickRange(undefined, 6)}
                  icon={CalendarRangeIcon}
                />
              </div>
            </div>

            {/* Calendar */}
            <SimpleCalendar
              selectedRange={localRange}
              onRangeChange={setLocalRange}
              tempStart={tempStart}
              onTempStartChange={setTempStart}
            />
          </div>

          {/* Footer */}
          <div className="flex gap-3 p-4 border-t">
            <button
              type="button"
              onClick={handleReset}
              className="flex-1 px-4 py-2.5 text-sm font-medium border border-gray-200 rounded-lg hover:bg-gray-50"
            >
              Reset
            </button>
            <button
              type="button"
              onClick={handleApply}
              className="flex-1 px-4 py-2.5 text-sm font-medium text-primary-foreground rounded-lg bg-primary"
            >
              Apply
            </button>
          </div>
        </div>
      </>
    );
  }

  // Top sheet for mobile - use FilterOverlayPortal for z-index isolation
  return (
    <FilterOverlayPortal isOpen={isOpen} onClose={onClose} primaryColor={primaryColor}>
      <div className="bg-white rounded-b-2xl shadow-xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Select Date Range</h2>
          <button
            type="button"
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full"
          >
            <XIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Quick Select */}
          <div className="space-y-2">
            <h4 className="font-medium text-sm text-gray-900">Quick Select</h4>
            <div className="grid grid-cols-2 gap-2">
              <QuickSelectButton
                label="Today"
                isActive={isToday}
                onClick={() => setQuickRange(undefined, undefined, true)}
                icon={CalendarIcon}
                primaryColor={primaryColor}
              />
              <QuickSelectButton
                label="Next 2 Weeks"
                isActive={isTwoWeeks}
                onClick={() => setQuickRange(2)}
                icon={CalendarRangeIcon}
                primaryColor={primaryColor}
              />
              <QuickSelectButton
                label="Next 3 Months"
                isActive={isThreeMonths}
                onClick={() => setQuickRange(undefined, 3)}
                icon={CalendarRangeIcon}
                primaryColor={primaryColor}
              />
              <QuickSelectButton
                label="Next 6 Months"
                isActive={isSixMonths}
                onClick={() => setQuickRange(undefined, 6)}
                icon={CalendarRangeIcon}
                primaryColor={primaryColor}
              />
            </div>
          </div>

          {/* Calendar */}
          <SimpleCalendar
            selectedRange={localRange}
            onRangeChange={setLocalRange}
            primaryColor={primaryColor}
            tempStart={tempStart}
            onTempStartChange={setTempStart}
          />
        </div>

        {/* Footer */}
        <div className="flex gap-3 p-4 border-t border-gray-200">
          <button
            type="button"
            onClick={handleReset}
            className="flex-1 px-4 py-2.5 text-sm font-medium border border-gray-200 rounded-lg hover:bg-gray-50 bg-white text-gray-900"
          >
            Reset
          </button>
          <button
            type="button"
            onClick={handleApply}
            className="flex-1 px-4 py-2.5 text-sm font-medium text-white rounded-lg"
            style={{ backgroundColor: primaryColor || "#03c2b9" }}
          >
            Apply
          </button>
        </div>
      </div>
    </FilterOverlayPortal>
  );
}

// Mobile filter sheet with two-level navigation (matching widget UX)
function MobileFilterSheet({
  isOpen,
  onClose,
  courses,
  locations,
  tutors,
  selectedCourseIds,
  selectedLocationIds,
  selectedTutorIds,
  selectedAvailability,
  onCourseChange,
  onLocationChange,
  onTutorChange,
  onAvailabilityChange,
  onReset,
  primaryColor,
}: Omit<TimetableFiltersProps, "dateRange" | "onDateRangeChange"> & {
  isOpen: boolean;
  onClose: () => void;
  primaryColor?: string;
}) {
  // Nested sheet states
  const [activeSheet, setActiveSheet] = useState<
    "event" | "availability" | "location" | "tutor" | null
  >(null);

  // Pending filter values (applied on close)
  const [pendingCourseIds, setPendingCourseIds] = useState(selectedCourseIds);
  const [pendingLocationIds, setPendingLocationIds] =
    useState(selectedLocationIds);
  const [pendingTutorIds, setPendingTutorIds] = useState(selectedTutorIds);
  const [pendingAvailability, setPendingAvailability] =
    useState(selectedAvailability);

  // Search states for each filter
  const [eventSearch, setEventSearch] = useState("");
  const [locationSearch, setLocationSearch] = useState("");
  const [tutorSearch, setTutorSearch] = useState("");

  // Sync pending values when props change or sheet opens
  useMemo(() => {
    if (isOpen) {
      setPendingCourseIds(selectedCourseIds);
      setPendingLocationIds(selectedLocationIds);
      setPendingTutorIds(selectedTutorIds);
      setPendingAvailability(selectedAvailability);
    }
  }, [
    isOpen,
    selectedCourseIds,
    selectedLocationIds,
    selectedTutorIds,
    selectedAvailability,
  ]);

  // Reset search when closing nested sheet
  const handleCloseNestedSheet = useCallback(() => {
    setEventSearch("");
    setLocationSearch("");
    setTutorSearch("");
    setActiveSheet(null);
  }, []);

  const courseOptions = courses.map((c) => ({ value: c._id, label: c.title }));
  const locationOptions = locations.map((l) => ({
    value: l._id,
    label: l.name,
  }));
  const tutorOptions = tutors.map((t) => ({
    value: t._id,
    label: t.fullName || t.name || "Unknown",
  }));

  // Filtered options based on search
  const filteredCourseOptions = useMemo(() => {
    if (!eventSearch.trim()) return courseOptions;
    const searchLower = eventSearch.toLowerCase();
    return courseOptions.filter((o) => o.label.toLowerCase().includes(searchLower));
  }, [courseOptions, eventSearch]);

  const filteredLocationOptions = useMemo(() => {
    if (!locationSearch.trim()) return locationOptions;
    const searchLower = locationSearch.toLowerCase();
    return locationOptions.filter((o) => o.label.toLowerCase().includes(searchLower));
  }, [locationOptions, locationSearch]);

  const filteredTutorOptions = useMemo(() => {
    if (!tutorSearch.trim()) return tutorOptions;
    const searchLower = tutorSearch.toLowerCase();
    return tutorOptions.filter((o) => o.label.toLowerCase().includes(searchLower));
  }, [tutorOptions, tutorSearch]);

  // Get selected items for chips display
  const selectedCourseItems = useMemo(() => {
    return pendingCourseIds
      .map((id) => courseOptions.find((o) => o.value === id))
      .filter(Boolean) as { value: string; label: string }[];
  }, [pendingCourseIds, courseOptions]);

  const selectedLocationItems = useMemo(() => {
    return pendingLocationIds
      .map((id) => locationOptions.find((o) => o.value === id))
      .filter(Boolean) as { value: string; label: string }[];
  }, [pendingLocationIds, locationOptions]);

  const selectedTutorItems = useMemo(() => {
    return pendingTutorIds
      .map((id) => tutorOptions.find((o) => o.value === id))
      .filter(Boolean) as { value: string; label: string }[];
  }, [pendingTutorIds, tutorOptions]);

  // Get display text for filter values
  const getEventText = useCallback(() => {
    if (pendingCourseIds.length === 0) return "All Events";
    if (pendingCourseIds.length === 1) {
      const course = courses.find((c) => c._id === pendingCourseIds[0]);
      return course?.title || "1 selected";
    }
    return `${pendingCourseIds.length} selected`;
  }, [pendingCourseIds, courses]);

  const getAvailabilityText = useCallback(() => {
    const option = AVAILABILITY_OPTIONS.find(
      (o) => o.value === pendingAvailability,
    );
    return option?.label || "All availability";
  }, [pendingAvailability]);

  const getLocationText = useCallback(() => {
    if (pendingLocationIds.length === 0) return "All Locations";
    if (pendingLocationIds.length === 1) {
      const location = locations.find((l) => l._id === pendingLocationIds[0]);
      return location?.name || "1 selected";
    }
    return `${pendingLocationIds.length} selected`;
  }, [pendingLocationIds, locations]);

  const getTutorText = useCallback(() => {
    if (pendingTutorIds.length === 0) return "All Tutors";
    if (pendingTutorIds.length === 1) {
      const tutor = tutors.find((t) => t._id === pendingTutorIds[0]);
      return tutor?.fullName || tutor?.name || "1 selected";
    }
    return `${pendingTutorIds.length} selected`;
  }, [pendingTutorIds, tutors]);

  const handleApplyFilters = () => {
    onCourseChange(pendingCourseIds);
    onLocationChange(pendingLocationIds);
    onTutorChange(pendingTutorIds);
    onAvailabilityChange(pendingAvailability);
    onClose();
  };

  const handleResetFilters = () => {
    setPendingCourseIds([]);
    setPendingLocationIds([]);
    setPendingTutorIds([]);
    setPendingAvailability("all");
    onReset();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <FilterOverlayPortal isOpen={isOpen} onClose={onClose} primaryColor={primaryColor}>
      {/* Main Filter Menu Sheet */}
      <div className="bg-white rounded-b-2xl shadow-xl max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Filters</h2>
          <button
            type="button"
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full"
          >
            <XIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Filter Menu Buttons */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {/* Event Type */}
          {courseOptions.length > 0 && (
            <FilterMenuButton
              icon={ListIcon}
              label="Event Type"
              value={getEventText()}
              onClick={() => setActiveSheet("event")}
            />
          )}

          {/* Availability */}
          <FilterMenuButton
            icon={TicketIcon}
            label="Availability"
            value={getAvailabilityText()}
            onClick={() => setActiveSheet("availability")}
          />

          {/* Location */}
          {locationOptions.length > 1 && (
            <FilterMenuButton
              icon={MapIcon}
              label="Location"
              value={getLocationText()}
              onClick={() => setActiveSheet("location")}
            />
          )}

          {/* Tutor */}
          {tutorOptions.length > 1 && (
            <FilterMenuButton
              icon={UserIcon}
              label="Tutor"
              value={getTutorText()}
              onClick={() => setActiveSheet("tutor")}
            />
          )}
        </div>

        {/* Footer */}
        <div className="flex gap-3 p-4 border-t border-gray-200">
          <button
            type="button"
            onClick={handleResetFilters}
            className="flex-1 px-4 py-2.5 text-sm font-medium border border-gray-200 rounded-lg hover:bg-gray-50 bg-white text-gray-900"
          >
            Reset All
          </button>
          <button
            type="button"
            onClick={handleApplyFilters}
            className="flex-1 px-4 py-2.5 text-sm font-medium text-white rounded-lg"
            style={{ backgroundColor: primaryColor || "#03c2b9" }}
          >
            Apply Filters
          </button>
        </div>
      </div>

      {/* Nested Sheets */}

      {/* Event Type Sheet */}
      <NestedFilterSheet
        isOpen={activeSheet === "event"}
        onClose={handleCloseNestedSheet}
        title="Event Type"
        showSearch={courseOptions.length > 5}
        searchPlaceholder="Search events..."
        primaryColor={primaryColor}
        searchValue={eventSearch}
        onSearchChange={setEventSearch}
        selectedItems={selectedCourseItems}
        onRemoveItem={(value) =>
          setPendingCourseIds(pendingCourseIds.filter((id) => id !== value))
        }
        onClearAll={() => setPendingCourseIds([])}
        onDone={handleCloseNestedSheet}
        showFooter={true}
      >
        <div className="space-y-1">
          {/* All Events option */}
          <button
            type="button"
            onClick={() => setPendingCourseIds([])}
            className={cn(
              "w-full flex items-center gap-3 px-3 py-3 rounded-lg text-left",
              pendingCourseIds.length === 0 ? "bg-gray-100" : "hover:bg-gray-50",
            )}
          >
            <span
              className={cn(
                "w-5 h-5 border rounded-full flex items-center justify-center shrink-0",
                pendingCourseIds.length === 0
                  ? "bg-gray-900 border-gray-900 text-white"
                  : "border-gray-300",
              )}
            >
              {pendingCourseIds.length === 0 && (
                <CheckIcon className="text-white" />
              )}
            </span>
            <span className="text-sm font-medium">All Events</span>
          </button>

          {filteredCourseOptions.map((option) => {
            const isSelected = pendingCourseIds.includes(option.value);
            return (
              <button
                key={option.value}
                type="button"
                onClick={() =>
                  setPendingCourseIds(
                    isSelected
                      ? pendingCourseIds.filter((id) => id !== option.value)
                      : [...pendingCourseIds, option.value],
                  )
                }
                className={cn(
                  "w-full flex items-center gap-3 px-3 py-3 rounded-lg text-left",
                  isSelected ? "bg-gray-100" : "hover:bg-gray-50",
                )}
              >
                <span
                  className={cn(
                    "w-5 h-5 border rounded flex items-center justify-center shrink-0",
                    isSelected
                      ? "bg-gray-900 border-gray-900 text-white"
                      : "border-gray-300",
                  )}
                >
                  {isSelected && <CheckIcon className="text-white" />}
                </span>
                <span className="text-sm">{option.label}</span>
              </button>
            );
          })}
        </div>
      </NestedFilterSheet>

      {/* Availability Sheet */}
      <NestedFilterSheet
        isOpen={activeSheet === "availability"}
        onClose={() => setActiveSheet(null)}
        title="Availability"
        primaryColor={primaryColor}
      >
        <div className="space-y-1">
          {AVAILABILITY_OPTIONS.map((option) => {
            const isSelected = pendingAvailability === option.value;
            return (
              <button
                key={option.value}
                type="button"
                onClick={() => {
                  setPendingAvailability(option.value);
                  setActiveSheet(null);
                }}
                className={cn(
                  "w-full flex items-center gap-3 px-3 py-3 rounded-lg text-left",
                  isSelected ? "bg-gray-100" : "hover:bg-gray-50",
                )}
              >
                <span
                  className={cn(
                    "w-5 h-5 border rounded-full flex items-center justify-center shrink-0",
                    isSelected
                      ? "bg-gray-900 border-gray-900 text-white"
                      : "border-gray-300",
                  )}
                >
                  {isSelected && <CheckIcon className="text-white" />}
                </span>
                <span className="text-sm">{option.label}</span>
              </button>
            );
          })}
        </div>
      </NestedFilterSheet>

      {/* Location Sheet */}
      <NestedFilterSheet
        isOpen={activeSheet === "location"}
        onClose={handleCloseNestedSheet}
        title="Location"
        showSearch={locationOptions.length > 5}
        searchPlaceholder="Search locations..."
        primaryColor={primaryColor}
        searchValue={locationSearch}
        onSearchChange={setLocationSearch}
        selectedItems={selectedLocationItems}
        onRemoveItem={(value) =>
          setPendingLocationIds(pendingLocationIds.filter((id) => id !== value))
        }
        onClearAll={() => setPendingLocationIds([])}
        onDone={handleCloseNestedSheet}
        showFooter={true}
      >
        <div className="space-y-1">
          {/* All Locations option */}
          <button
            type="button"
            onClick={() => setPendingLocationIds([])}
            className={cn(
              "w-full flex items-center gap-3 px-3 py-3 rounded-lg text-left",
              pendingLocationIds.length === 0
                ? "bg-gray-100"
                : "hover:bg-gray-50",
            )}
          >
            <span
              className={cn(
                "w-5 h-5 border rounded-full flex items-center justify-center shrink-0",
                pendingLocationIds.length === 0
                  ? "bg-gray-900 border-gray-900 text-white"
                  : "border-gray-300",
              )}
            >
              {pendingLocationIds.length === 0 && (
                <CheckIcon className="text-white" />
              )}
            </span>
            <span className="text-sm font-medium">All Locations</span>
          </button>

          {filteredLocationOptions.map((option) => {
            const isSelected = pendingLocationIds.includes(option.value);
            return (
              <button
                key={option.value}
                type="button"
                onClick={() =>
                  setPendingLocationIds(
                    isSelected
                      ? pendingLocationIds.filter((id) => id !== option.value)
                      : [...pendingLocationIds, option.value],
                  )
                }
                className={cn(
                  "w-full flex items-center gap-3 px-3 py-3 rounded-lg text-left",
                  isSelected ? "bg-gray-100" : "hover:bg-gray-50",
                )}
              >
                <span
                  className={cn(
                    "w-5 h-5 border rounded flex items-center justify-center shrink-0",
                    isSelected
                      ? "bg-gray-900 border-gray-900 text-white"
                      : "border-gray-300",
                  )}
                >
                  {isSelected && <CheckIcon className="text-white" />}
                </span>
                <span className="text-sm">{option.label}</span>
              </button>
            );
          })}
        </div>
      </NestedFilterSheet>

      {/* Tutor Sheet */}
      <NestedFilterSheet
        isOpen={activeSheet === "tutor"}
        onClose={handleCloseNestedSheet}
        title="Tutor"
        showSearch={tutorOptions.length > 5}
        searchPlaceholder="Search tutors..."
        primaryColor={primaryColor}
        searchValue={tutorSearch}
        onSearchChange={setTutorSearch}
        selectedItems={selectedTutorItems}
        onRemoveItem={(value) =>
          setPendingTutorIds(pendingTutorIds.filter((id) => id !== value))
        }
        onClearAll={() => setPendingTutorIds([])}
        onDone={handleCloseNestedSheet}
        showFooter={true}
      >
        <div className="space-y-1">
          {/* All Tutors option */}
          <button
            type="button"
            onClick={() => setPendingTutorIds([])}
            className={cn(
              "w-full flex items-center gap-3 px-3 py-3 rounded-lg text-left",
              pendingTutorIds.length === 0 ? "bg-gray-100" : "hover:bg-gray-50",
            )}
          >
            <span
              className={cn(
                "w-5 h-5 border rounded-full flex items-center justify-center shrink-0",
                pendingTutorIds.length === 0
                  ? "bg-gray-900 border-gray-900 text-white"
                  : "border-gray-300",
              )}
            >
              {pendingTutorIds.length === 0 && (
                <CheckIcon className="text-white" />
              )}
            </span>
            <span className="text-sm font-medium">All Tutors</span>
          </button>

          {filteredTutorOptions.map((option) => {
            const isSelected = pendingTutorIds.includes(option.value);
            return (
              <button
                key={option.value}
                type="button"
                onClick={() =>
                  setPendingTutorIds(
                    isSelected
                      ? pendingTutorIds.filter((id) => id !== option.value)
                      : [...pendingTutorIds, option.value],
                  )
                }
                className={cn(
                  "w-full flex items-center gap-3 px-3 py-3 rounded-lg text-left",
                  isSelected ? "bg-gray-100" : "hover:bg-gray-50",
                )}
              >
                <span
                  className={cn(
                    "w-5 h-5 border rounded flex items-center justify-center shrink-0",
                    isSelected
                      ? "bg-gray-900 border-gray-900 text-white"
                      : "border-gray-300",
                  )}
                >
                  {isSelected && <CheckIcon className="text-white" />}
                </span>
                <span className="text-sm">{option.label}</span>
              </button>
            );
          })}
        </div>
      </NestedFilterSheet>
    </FilterOverlayPortal>
  );
}

function WeekFilter({
  onDateRangeChange,
  isActive,
  onActiveChange,
}: {
  onDateRangeChange: (range: DateRange) => void;
  /** When false, the timetable shows the next N upcoming sessions unfiltered. */
  isActive: boolean;
  /** Toggle whether the week filter is constraining results. */
  onActiveChange: (active: boolean) => void;
}) {
  const [currentWeek, setCurrentWeek] = useState(() => startOfWeek(new Date()));
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const getWeekRange = useCallback(
    (date: Date): DateRange => ({
      from: startOfWeek(date),
      to: endOfWeek(date),
    }),
    [],
  );

  // Switch to a specific week (turning the filter on if needed) and notify the parent.
  const handleSelectWeek = useCallback(
    (date: Date) => {
      const newWeek = startOfWeek(date);
      setCurrentWeek(newWeek);
      onDateRangeChange(getWeekRange(date));
      if (!isActive) onActiveChange(true);
    },
    [onDateRangeChange, getWeekRange, isActive, onActiveChange],
  );

  // Clear the week filter and return to the unfiltered "next N sessions" view.
  const handleSelectAllUpcoming = useCallback(() => {
    onActiveChange(false);
    setCurrentWeek(startOfWeek(new Date()));
  }, [onActiveChange]);

  const isCurrentWeek = isSameWeek(currentWeek, new Date());
  const weekRange = getWeekRange(currentWeek);

  const formatWeekDate = (date: Date) =>
    date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });

  const weekRangeText = `${formatWeekDate(weekRange.from)} - ${formatWeekDate(weekRange.to)}`;

  const weeksList = useMemo(() => {
    const weeks = [];
    const today = new Date();
    for (let i = 0; i <= 52; i++) {
      const weekDate = addWeeks(today, i);
      const range = getWeekRange(weekDate);
      weeks.push({
        date: startOfWeek(weekDate),
        label: `${formatWeekDate(range.from)} - ${formatWeekDate(range.to)}`,
        isCurrent: i === 0,
      });
    }
    return weeks;
  }, [getWeekRange]);

  // Previous arrow is disabled in the unfiltered view (nothing to go back to)
  // and on the first allowed week (today's week).
  const isPrevDisabled = !isActive || isCurrentWeek;

  const handlePrevClick = useCallback(() => {
    if (!isActive || isCurrentWeek) return;
    handleSelectWeek(addWeeks(currentWeek, -1));
  }, [isActive, isCurrentWeek, currentWeek, handleSelectWeek]);

  // Next arrow either kicks off filtering (start with the current week) or advances by one.
  const handleNextClick = useCallback(() => {
    if (!isActive) {
      handleSelectWeek(new Date());
      return;
    }
    handleSelectWeek(addWeeks(currentWeek, 1));
  }, [isActive, currentWeek, handleSelectWeek]);

  return (
    <div className="flex items-center gap-1.5">
      <button
        type="button"
        onClick={handlePrevClick}
        disabled={isPrevDisabled}
        className={cn(
          "flex items-center gap-1 px-2.5 py-2 text-sm bg-white border rounded-full transition-colors shrink-0",
          isPrevDisabled
            ? "border-gray-100 text-gray-300 cursor-not-allowed"
            : "border-gray-200 hover:bg-gray-50 text-gray-600",
        )}
      >
        <ChevronLeftIcon className="h-4 w-4" />
        <span className="hidden sm:inline">Previous week</span>
      </button>

      <div className="relative">
        <button
          type="button"
          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
          className="flex items-center gap-2 px-3 py-2 text-sm bg-white border border-gray-200 rounded-full hover:bg-gray-50 transition-colors min-w-[180px] sm:min-w-[220px]"
        >
          <div className="flex flex-col items-start flex-1">
            {isActive ? (
              <>
                <span className="font-semibold text-gray-900">
                  {weekRangeText}
                </span>
                {isCurrentWeek && (
                  <span className="text-xs text-gray-500">Current week</span>
                )}
              </>
            ) : (
              <>
                <span className="font-semibold text-gray-900">
                  All upcoming sessions
                </span>
                <span className="text-xs text-gray-500">
                  Next {LONDON_LADY_INITIAL_SESSION_LIMIT} sessions
                </span>
              </>
            )}
          </div>
          <ChevronDownIcon
            className={cn(
              "shrink-0 transition-transform",
              isDropdownOpen && "rotate-180",
            )}
          />
        </button>

        {isDropdownOpen && (
          <>
            <div
              className="fixed inset-0 z-40"
              onClick={() => setIsDropdownOpen(false)}
            />
            <div className="absolute top-full left-1/2 -translate-x-1/2 sm:left-0 sm:translate-x-0 mt-1 z-50 min-w-[240px] max-h-[300px] overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg">
              <div className="p-1">
                <button
                  type="button"
                  onClick={() => {
                    handleSelectAllUpcoming();
                    setIsDropdownOpen(false);
                  }}
                  className={cn(
                    "w-full px-3 py-2 text-left text-sm rounded",
                    !isActive
                      ? "bg-gray-100 font-semibold text-gray-900"
                      : "text-gray-700 hover:bg-gray-50",
                  )}
                >
                  <div className="flex flex-col">
                    <span>All upcoming sessions</span>
                    <span className="text-xs text-gray-500">
                      Next {LONDON_LADY_INITIAL_SESSION_LIMIT} sessions
                    </span>
                  </div>
                </button>
                {weeksList.map((week) => {
                  const isSelected =
                    isActive && isSameWeek(currentWeek, week.date);
                  return (
                    <button
                      key={week.date.toISOString()}
                      type="button"
                      onClick={() => {
                        handleSelectWeek(week.date);
                        setIsDropdownOpen(false);
                      }}
                      className={cn(
                        "w-full px-3 py-2 text-left text-sm rounded",
                        isSelected
                          ? "bg-gray-100 font-semibold text-gray-900"
                          : "text-gray-700 hover:bg-gray-50",
                      )}
                    >
                      <div className="flex flex-col">
                        <span>{week.label}</span>
                        {week.isCurrent && (
                          <span className="text-xs text-gray-500">
                            Current week
                          </span>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </>
        )}
      </div>

      <button
        type="button"
        onClick={handleNextClick}
        className="flex items-center gap-1 px-2.5 py-2 text-sm bg-white border border-gray-200 rounded-full hover:bg-gray-50 text-gray-600 transition-colors shrink-0"
      >
        <span className="hidden sm:inline">
          {isActive ? "Next week" : "View by week"}
        </span>
        <ChevronRightIcon className="h-4 w-4" />
      </button>
    </div>
  );
}


export function TimetableFilters(props: TimetableFiltersProps) {
  const {
    courses,
    locations,
    tutors,
    selectedCourseIds,
    selectedLocationIds,
    selectedTutorIds,
    selectedAvailability,
    dateRange,
    onCourseChange,
    onLocationChange,
    onTutorChange,
    onAvailabilityChange,
    onDateRangeChange,
    onReset,
    primaryColor = "#03c2b9",
    businessSlug,
    apiUrl,
    showWeekFilter = false,
    weekFilterActive = false,
    onWeekFilterActiveChange,
  } = props;

  // Default to a no-op so the prop is optional but always callable inside WeekFilter.
  const handleWeekFilterActiveChange = useCallback(
    (active: boolean) => {
      onWeekFilterActiveChange?.(active);
    },
    [onWeekFilterActiveChange],
  );

  const [isMobileSheetOpen, setIsMobileSheetOpen] = useState(false);
  const [isDateRangeOpen, setIsDateRangeOpen] = useState(false);
  const [isEventDropdownOpen, setIsEventDropdownOpen] = useState(false);

  // Detect if mobile (simple check for SSR compatibility)
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 640);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const courseOptions = useMemo(
    () => courses.map((c) => ({ value: c._id, label: c.title })),
    [courses],
  );
  const locationOptions = useMemo(
    () => locations.map((l) => ({ value: l._id, label: l.name })),
    [locations],
  );
  const tutorOptions = useMemo(
    () =>
      tutors.map((t) => ({
        value: t._id,
        label: t.fullName || t.name || "Unknown",
      })),
    [tutors],
  );

  const activeFilterCount = [
    selectedCourseIds.length > 0,
    selectedLocationIds.length > 0,
    selectedTutorIds.length > 0,
    selectedAvailability !== "all",
  ].filter(Boolean).length;

  const dateRangeText = formatDateRange(dateRange.from, dateRange.to);

  return (
    <div className="bg-white border-b border-gray-200 px-4 py-3">
      {/* Mobile: Filter button + Date Range button */}
      <div className="flex flex-col gap-2 sm:hidden">
        {showWeekFilter ? (
          <>
            {courseOptions.length > 0 && (
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setIsEventDropdownOpen(!isEventDropdownOpen)}
                  className="w-full flex items-center justify-between gap-2 px-4 py-2.5 bg-white border border-gray-200 rounded-full text-sm font-medium hover:bg-gray-50"
                >
                  <div className="flex items-center gap-2">
                    <ListIcon className="h-4 w-4" />
                    <span className="truncate">
                      {selectedCourseIds.length > 0
                        ? courses.find((c) => c._id === selectedCourseIds[0])
                            ?.title || "All events"
                        : "All events"}
                    </span>
                  </div>
                  <ChevronDownIcon className="h-4 w-4 shrink-0" />
                </button>
                {isEventDropdownOpen && (
                  <>
                    <div
                      className="fixed inset-0 z-40"
                      onClick={() => setIsEventDropdownOpen(false)}
                    />
                    <div className="absolute top-full left-0 right-0 mt-1 z-50 bg-white border border-gray-200 rounded-lg shadow-lg max-h-[300px] overflow-y-auto">
                      <div className="p-1">
                        <button
                          type="button"
                          onClick={() => {
                            onCourseChange([]);
                            setIsEventDropdownOpen(false);
                          }}
                          className={cn(
                            "w-full px-3 py-2 text-left text-sm rounded",
                            selectedCourseIds.length === 0
                              ? "bg-gray-100 font-medium text-gray-900"
                              : "text-gray-700 hover:bg-gray-50",
                          )}
                        >
                          All events
                        </button>
                        {courseOptions.map((option) => (
                          <button
                            key={option.value}
                            type="button"
                            onClick={() => {
                              onCourseChange([option.value]);
                              setIsEventDropdownOpen(false);
                            }}
                            className={cn(
                              "w-full px-3 py-2 text-left text-sm rounded",
                              selectedCourseIds.includes(option.value)
                                ? "bg-gray-100 font-medium text-gray-900"
                                : "text-gray-700 hover:bg-gray-50",
                            )}
                          >
                            {option.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
            <WeekFilter
              onDateRangeChange={onDateRangeChange}
              isActive={weekFilterActive}
              onActiveChange={handleWeekFilterActiveChange}
            />
          </>
        ) : (
          <>
            <button
              type="button"
              onClick={() => setIsMobileSheetOpen(true)}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-white border border-gray-200 rounded-full text-sm font-medium hover:bg-gray-50"
            >
              <FilterIcon />
              <span>Filters</span>
              {activeFilterCount > 0 && (
                <span className="px-2 py-0.5 text-xs text-primary-foreground rounded-full bg-primary">
                  {activeFilterCount}
                </span>
              )}
            </button>

            <button
              type="button"
              onClick={() => setIsDateRangeOpen(true)}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-white border border-gray-200 rounded-full text-sm font-medium hover:bg-gray-50"
            >
              <CalendarIcon />
              <span>{dateRangeText}</span>
            </button>

          </>
        )}
      </div>

      {/* Mobile overlays - only mounted on mobile to avoid portaling on top of desktop UI.
          The wrapping `sm:hidden` div above only hides via display:none and does NOT unmount
          these portals, so we gate them on the runtime `isMobile` value here. */}
      {isMobile && !showWeekFilter && (
        <>
          <MobileFilterOverlay
            courses={courses}
            locations={locations}
            tutors={tutors}
            selectedCourseIds={selectedCourseIds}
            selectedLocationIds={selectedLocationIds}
            selectedTutorIds={selectedTutorIds}
            selectedAvailability={selectedAvailability}
            onCourseChange={onCourseChange}
            onLocationChange={onLocationChange}
            onTutorChange={onTutorChange}
            onAvailabilityChange={onAvailabilityChange}
            onReset={onReset}
            isOpen={isMobileSheetOpen}
            onClose={() => setIsMobileSheetOpen(false)}
            primaryColor={primaryColor}
            businessSlug={businessSlug}
            apiUrl={apiUrl}
          />

          <MobileDateRangeOverlay
            isOpen={isDateRangeOpen}
            onClose={() => setIsDateRangeOpen(false)}
            dateRange={dateRange}
            onDateRangeChange={onDateRangeChange}
            primaryColor={primaryColor}
            businessSlug={businessSlug}
            apiUrl={apiUrl}
          />
        </>
      )}

      {/* Desktop: Inline filters + Date Range */}
      <div className="hidden sm:flex flex-wrap items-center gap-2">
        {/* Event Type */}
        {courseOptions.length > 0 && (
          <Dropdown
            label="Event"
            options={courseOptions}
            selectedValues={selectedCourseIds}
            onChange={onCourseChange}
            placeholder="All events"
            multiple
          />
        )}

        {/* Availability - hidden in week filter mode */}
        {!showWeekFilter && (
          <Dropdown
            label="Availability"
            options={AVAILABILITY_OPTIONS}
            selectedValues={[selectedAvailability]}
            onChange={(values) =>
              onAvailabilityChange((values[0] || "all") as AvailabilityFilter)
            }
            placeholder="All availability"
          />
        )}

        {/* Location - hidden in week filter mode */}
        {!showWeekFilter && locationOptions.length > 1 && (
          <Dropdown
            label="Location"
            options={locationOptions}
            selectedValues={selectedLocationIds}
            onChange={onLocationChange}
            placeholder="All locations"
            multiple
          />
        )}

        {/* Tutor - hidden in week filter mode */}
        {!showWeekFilter && tutorOptions.length > 1 && (
          <Dropdown
            label="Tutor"
            options={tutorOptions}
            selectedValues={selectedTutorIds}
            onChange={onTutorChange}
            placeholder="All tutors"
            multiple
          />
        )}

        {showWeekFilter ? (
          <div className="ml-auto">
            <WeekFilter
              onDateRangeChange={onDateRangeChange}
              isActive={weekFilterActive}
              onActiveChange={handleWeekFilterActiveChange}
            />
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setIsDateRangeOpen(true)}
            className="flex items-center gap-2 px-3 py-2 text-sm bg-white border border-gray-200 rounded-full hover:bg-gray-50 transition-colors ml-auto"
          >
            <CalendarIcon className="h-4 w-4" />
            <span>{dateRangeText}</span>
          </button>
        )}

        {/* Reset button - hidden in week filter mode */}
        {!showWeekFilter && activeFilterCount > 0 && (
          <button
            type="button"
            onClick={onReset}
            className="px-3 py-2 text-sm text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full"
          >
            Reset
          </button>
        )}
      </div>

      {/* Date Range Sheet - Desktop only (mobile uses MobileDateRangeOverlay) */}
      {!isMobile && !showWeekFilter && (
        <DateRangeSheet
          isOpen={isDateRangeOpen}
          onClose={() => setIsDateRangeOpen(false)}
          dateRange={dateRange}
          onDateRangeChange={onDateRangeChange}
          side="right"
          primaryColor={primaryColor}
        />
      )}
    </div>
  );
}
